// JavaScript
// app/javascript/controllers/faa_search_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["input", "results"]
    static values = {
        url: String,
        min: { type: Number, default: 2 },
        delay: { type: Number, default: 200 },
        // Optional selectors if your first/last fields have custom IDs
        firstFieldSelector: String,
        lastFieldSelector: String
    }

    connect() {
        this.abortController = null
        this._debounced = this._debounce(this._fetch.bind(this), this.delayValue)

        // Delegate change events from the results container
        this._onResultsChange = (e) => {
            const cb = e.target?.closest?.(".faa-master-checkbox")
            if (!cb) return
            // Populate from the first checked checkbox (stable behavior when multiple are selected)
            const firstChecked = this.resultsTarget?.querySelector?.(".faa-master-checkbox:checked")
            const name = (firstChecked?.dataset?.name || cb.dataset?.name || "").trim()
            if (name) this._populateNameFields(name)
        }
        if (this.hasResultsTarget) {
            this.resultsTarget.addEventListener("change", this._onResultsChange)
        }
    }

    disconnect() {
        this._abortActive()
        if (this.hasResultsTarget && this._onResultsChange) {
            this.resultsTarget.removeEventListener("change", this._onResultsChange)
        }
    }

    onInput() {
        const q = (this.inputTarget?.value || "").trim()
        if (q.length < this.minValue) {
            this._clearResults()
            return
        }
        this._debounced(q)
    }

    async _fetch(q) {
        if (!this.hasUrlValue || !this.urlValue) return
        this._abortActive()
        this.abortController = new AbortController()

        try {
            const url = new URL(this.urlValue, window.location.origin)
            url.searchParams.set("q", q)
            const res = await fetch(url.toString(), {
                headers: { Accept: "application/json" },
                signal: this.abortController.signal
            })
            if (!res.ok) throw new Error(`Search failed: ${res.status}`)
            const data = await res.json()
            this._render(Array.isArray(data) ? data : (data.results || []))
        } catch (e) {
            if (e.name !== "AbortError") console.error("faa-search fetch error:", e)
            this._clearResults()
        }
    }

    _render(items) {
        const esc = s =>
            String(s ?? "")
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#39;")

        const rows = (items || []).map(it => {
            const name = it?.name ?? ""
            const nnum = it?.n_number ?? ""
            return `
        <tr>
          <td class="px-3 py-2">${esc(name)}</td>
          <td class="px-3 py-2">${esc(nnum)}</td>
          <td class="px-3 py-2 text-center">
            <input type="checkbox"
              class="faa-master-checkbox"
              name="user[selected_n_numbers][]"
              value="${esc(it.n_number || "")}"
              data-name="${esc(it.name || "")}"
              ${it.n_number ? "" : "disabled"} />
          </td>
        </tr>
      `
        }).join("")

        if (!this.hasResultsTarget) return

        if (!rows) {
            this.resultsTarget.innerHTML = `<div class="px-3 py-2 text-gray-500">No matches.</div>`
            return
        }

        this.resultsTarget.innerHTML = `
      <table class="min-w-full border rounded-md">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-3 py-2 text-left">Name</th>
            <th class="px-3 py-2 text-left">N-Number</th>
            <th class="px-3 py-2 text-center">Select</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    `
    }

    _populateNameFields(fullName) {
        const { first, last } = this._splitName(fullName)
        const firstEl = this._resolveFieldElement("first")
        const lastEl  = this._resolveFieldElement("last")
        if (firstEl) this._setValue(firstEl, first)
        if (lastEl)  this._setValue(lastEl, last)
    }

    _resolveFieldElement(kind) {
        if (kind === "first" && this.hasFirstFieldSelectorValue) {
            const el = document.querySelector(this.firstFieldSelectorValue)
            if (el) return el
        }
        if (kind === "last" && this.hasLastFieldSelectorValue) {
            const el = document.querySelector(this.lastFieldSelectorValue)
            if (el) return el
        }
        const defaults = kind === "first"
            ? ['input[name="user[first_name]"]', "#user_first_name"]
            : ['input[name="user[last_name]"]', "#user_last_name"]
        for (const sel of defaults) {
            const el = document.querySelector(sel)
            if (el) return el
        }
        return null
    }

    _splitName(label) {
        const s = String(label || "").trim().replace(/\s+/g, " ")
        if (!s) return { first: "", last: "" }
        if (s.includes(",")) {
            const parts = s.split(",")
            return { first: parts.slice(1).join(" ").trim(), last: parts[0].trim() }
        }
        const tokens = s.split(" ")
        if (tokens.length === 1) return { first: tokens[0], last: "" }
        // FAA often provides "LAST FIRST M..." — treat first token as last name
        return { first: tokens.slice(1).join(" "), last: tokens[0] }
    }

    _setValue(el, value) {
        el.value = value || ""
        el.dispatchEvent(new Event("input", { bubbles: true }))
        el.dispatchEvent(new Event("change", { bubbles: true }))
    }

    _clearResults() {
        if (this.hasResultsTarget) this.resultsTarget.innerHTML = ""
    }

    _abortActive() {
        if (this.abortController) {
            try { this.abortController.abort() } catch (_) {}
            this.abortController = null
        }
    }

    _debounce(fn, wait) {
        let t = null
        return (...args) => {
            clearTimeout(t)
            t = setTimeout(() => fn(...args), wait)
        }
    }
}